import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { chatAPI, ChatSession, UserInfo, ProcessResponse, DocumentResponse } from '../services/apiService';
import Canvas from './Canvas';
import ChatSidebar from './ChatSidebar';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ThemeSettings from './ThemeSettings';
import AdminModal from './AdminModal';

interface ChatbotUIProps {
  className?: string;
  onLogout: () => void;
}

interface LocalMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  documents?: DocumentResponse[];
}

const ChatbotUI: React.FC<ChatbotUIProps> = ({ className, onLogout }) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [canvasContent, setCanvasContent] = useState<string | null>(null);
  const [currentSessionUuid, setCurrentSessionUuid] = useState<string | null>(null);
  
  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Theme settings state
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  
  // Admin modal state
  const [showAdminModal, setShowAdminModal] = useState(false);
  
  // Remove filter states - no longer needed
  
  // Response style selector with localStorage persistence
  const [responseStyle, setResponseStyle] = useState<'concise' | 'normal' | 'descriptive'>(() => {
    try {
      const saved = localStorage.getItem('chatResponseStyle');
      return (saved as 'concise' | 'normal' | 'descriptive') || 'concise';
    } catch {
      return 'concise';
    }
  });

  // Source filter with localStorage persistence (default to both ACAS and Policy)
  const [sourceFilter, setSourceFilter] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('chatSourceFilter');
      return saved ? JSON.parse(saved) : ['Policy'];
    } catch {
      return [ 'Policy'];
    }
  });

  // Save response style to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('chatResponseStyle', responseStyle);
    } catch (error) {
      console.warn('Failed to save response style to localStorage:', error);
    }
  }, [responseStyle]);

  // Save source filter to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('chatSourceFilter', JSON.stringify(sourceFilter));
    } catch (error) {
      console.warn('Failed to save source filter to localStorage:', error);
    }
  }, [sourceFilter]);
  

  // Load user info and chat sessions on mount
  useEffect(() => {
    loadUserData();
  }, []);

  // Handle URL parameter changes
  useEffect(() => {
    console.log('URL Effect:', { sessionId, chatSessionsLength: chatSessions.length, activeSessionId });
    
    if (sessionId && chatSessions.length > 0) {
      // Find session by UUID
      const session = chatSessions.find(s => s.id === sessionId);
      console.log('Found session:', session);
      
      if (session && session.id !== activeSessionId) {
        console.log('Switching to session:', session.id);
        switchToSession(session.id);
      }
    } else if (!sessionId) {
      // Clear active session when on home page
      if (activeSessionId) {
        console.log('Clearing active session for home page');
        setActiveSessionId(null);
        setMessages([]);
      }
    }
  }, [sessionId, chatSessions.length]); // Removed activeSessionId and navigate from dependencies

  const loadUserData = async () => {
    try {
      const user = await chatAPI.getCurrentUser();
      setCurrentUser(user);
      
      const sessions = await chatAPI.getChatSessions();
      setChatSessions(sessions);
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  // Load chat history when switching sessions
  const switchToSession = async (sessionId: string) => {
    try {
      const history = await chatAPI.getChatHistory(sessionId);
      const localMessages: LocalMessage[] = history.messages.map(msg => ({
        id: msg.id.toString(),
        content: msg.content,
        role: msg.role as 'user' | 'assistant',
        documents: msg.documents // Include documents from chat history
      }));
      
      setMessages(localMessages);
      setActiveSessionId(sessionId);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  // Handle session navigation from sidebar
  const handleSessionNavigation = (sessionId: string) => {
    navigate(`/chat/${sessionId}`);
  };

  // Create new chat session
  const createNewSession = () => {
    setMessages([]);
    setActiveSessionId(null);
    setCurrentSessionUuid(null);
    // Navigate to home page for new session
    navigate('/', { replace: true });
  };

  // Delete chat session
  const deleteSession = async (sessionId: string) => {
    try {
      await chatAPI.deleteChatSession(sessionId);
      
      // Remove from local state
      setChatSessions(prev => prev.filter(s => s.id !== sessionId));
      
      // If it was the active session, clear messages
      if (activeSessionId === sessionId) {
        setMessages([]);
        setActiveSessionId(null);
      }
      
      setSessionToDelete(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  // Show delete confirmation
  const showDeleteConfirmation = (sessionId: string) => {
    setSessionToDelete(sessionId);
  };

  // Cancel delete
  const cancelDelete = () => {
    setSessionToDelete(null);
  };

  // Handle canvas open
  const handleCanvasOpen = (content: string) => {
    setCanvasContent(content);
  };

  // Handle input change
  const handleInputChange = (value: string) => {
    setInput(value);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Generate UUID for new session if this is the first message
    let sessionUuid = activeSessionId;
    if (!activeSessionId) {
      sessionUuid = nanoid();
      setCurrentSessionUuid(sessionUuid);
      setActiveSessionId(sessionUuid);  // Set the active session ID immediately
      // Update URL with the new session UUID
      navigate(`/chat/${sessionUuid}`, { replace: true });
    }

    // Add user message to UI
    const userMessage: LocalMessage = {
      id: Date.now().toString(),
      content: input,
      role: 'user'
    };
    
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    
    const userInput = input;
    setInput('');
    setIsLoading(true);

    // Create placeholder for assistant message
    const assistantPlaceholder: LocalMessage = {
      id: (Date.now() + 1).toString(),
      content: '',
      role: 'assistant'
    };
    
    setMessages([...updatedMessages, assistantPlaceholder]);

    // Stream response from API with RAG functionality
    let streamedContent = '';
    let receivedDocuments: DocumentResponse[] = [];
    
    try {
      await chatAPI.streamChat(sessionUuid, userInput, {
        onStart: () => {
          // Response started
        },
        onToken: (token: string) => {
          // Add new token to the content
          streamedContent += token;
          
          // Update immediately for smooth streaming - no throttling
          setMessages(prev => {
            const updated = [...prev];
            const lastMessageIndex = updated.length - 1;
            if (updated[lastMessageIndex] && updated[lastMessageIndex].role === 'assistant') {
              updated[lastMessageIndex] = {
                ...updated[lastMessageIndex],
                content: streamedContent,
                documents: receivedDocuments
              };
            }
            return updated;
          });
        },
        onDocuments: (documents: DocumentResponse[]) => {
          // Store documents when received
          receivedDocuments = documents;
          
          // Update the assistant message with documents
          setMessages(prev => {
            const updated = [...prev];
            const lastMessageIndex = updated.length - 1;
            if (updated[lastMessageIndex] && updated[lastMessageIndex].role === 'assistant') {
              updated[lastMessageIndex] = {
                ...updated[lastMessageIndex],
                content: streamedContent,
                documents: receivedDocuments
              };
            }
            return updated;
          });
        },
        onFinish: (sessionId?: string) => {
          // Final update with complete content
          setMessages(prev => {
            const updated = [...prev];
            const lastMessageIndex = updated.length - 1;
            if (updated[lastMessageIndex] && updated[lastMessageIndex].role === 'assistant') {
              updated[lastMessageIndex] = {
                ...updated[lastMessageIndex],
                content: streamedContent,
                documents: receivedDocuments
              };
            }
            return updated;
          });
          
          setIsLoading(false);
          
          // Update active session ID if this was a new session
          if (sessionId && !activeSessionId) {
            setActiveSessionId(sessionId);
            // Update URL with the actual session ID
            navigate(`/chat/${sessionId}`, { replace: true });
            // Reload sessions to get the new one
            loadUserData();
          }
        },
        onError: (error: string) => {
          setIsLoading(false);
          console.error('Chat error:', error);
          
          // Remove the placeholder message and show error
          setMessages(prev => prev.slice(0, -1));
          
          // Add error message
          const errorMessage: LocalMessage = {
            id: Date.now().toString(),
            content: `Error: ${error}`,
            role: 'assistant'
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      }, responseStyle, sourceFilter);
    } catch (error) {
      setIsLoading(false);
      console.error('Submit error:', error);
    }
  };
  // width: 100%;
  //   max-width: 100%;
  //   overflow-x: hidden;
  return (
    <div className={`flex h-screen theme-bg-secondary overflow-x-hidden ${className}`}>
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`${
        isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <ChatSidebar
          currentUser={currentUser}
          chatSessions={chatSessions}
          activeSessionId={activeSessionId}
          sessionToDelete={sessionToDelete}
          onCreateNewSession={createNewSession}
          onSwitchToSession={handleSessionNavigation}
          onDeleteSession={deleteSession}
          onShowDeleteConfirmation={showDeleteConfirmation}
          onCancelDelete={cancelDelete}
          onLogout={onLogout}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
          onOpenThemeSettings={() => setShowThemeSettings(true)}
          onOpenAdminPanel={() => setShowAdminModal(true)}
        />
      </div>
      
      {/* Main chat area */}
      <div className="flex-1 flex flex-col theme-bg relative">
        {/* Mobile Header with Hamburger */}
        <div className="lg:hidden h-14 border-b theme-border flex items-center px-4 theme-bg-secondary">
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="p-2 theme-text hover:theme-bg-tertiary rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="ml-3 theme-text font-medium">
            {activeSessionId ? (
              chatSessions.find(s => s.id === activeSessionId)?.title || 'Chat Session'
            ) : (
              'New Chat Session'
            )}
          </div>
        </div>
        
        {/* Desktop Header */}
        <div className="hidden lg:block">
          <ChatHeader
            activeSessionId={activeSessionId}
            chatSessions={chatSessions}
          />
        </div>
        

        {/* Messages */}
        <ChatMessages
          messages={messages}
          isLoading={isLoading}
          onCanvasOpen={handleCanvasOpen}
          citationProcessingEnabled={true}
        />

        {/* Input */}
        <ChatInput
          input={input}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          hasMessages={messages.length > 0}
          responseStyle={responseStyle}
          onResponseStyleChange={setResponseStyle}
          sourceFilter={sourceFilter}
          onSourceFilterChange={setSourceFilter}
          userCompanyId={currentUser?.company_id || null}
        />
      </div>
      
      {/* Canvas Modal */}
      {canvasContent && (
        <Canvas 
          content={canvasContent} 
          onClose={() => setCanvasContent(null)} 
        />
      )}
      
      {/* Theme Settings Modal */}
      <ThemeSettings 
        isOpen={showThemeSettings} 
        onClose={() => setShowThemeSettings(false)} 
      />
      
      {/* Admin Modal */}
      <AdminModal 
        isOpen={showAdminModal} 
        onClose={() => setShowAdminModal(false)} 
      />
    </div>
  );
};

export default ChatbotUI;
